import { useApolloClient } from '@apollo/client';
import { color, HStack, Text } from '@starrez/starryui9';
import { ColDef, ColumnState } from 'ag-grid-community';
import React, { createRef, useEffect, useRef } from 'react';
import { ErrorDisplay } from '../../../../.starryUI/Errors/ErrorDisplay';
import { BarFilter, FilterBar, IAvailableFilter, ICurrentFilter, IData, IFilterBarRef } from '../../../../.starryUI/filters/filterBar';
import { ButtonBar, FilterChange } from '../../../../.starryUI/grids/buttonBar/ButtonBar';
import { IManagedColumn, ManageColumnButton } from '../../../../.starryUI/grids/buttonBar/ManageColumnButton';
import { RefreshButton } from '../../../../.starryUI/grids/buttonBar/RefreshButton';
import { EmailColumn } from '../../../../.starryUI/grids/cells/EmailColumn/EmailColumn';
import { EntryStatusColumn } from '../../../../.starryUI/grids/cells/entryStatusColumn/EntryStatusColumn';
import { FlagColumn } from '../../../../.starryUI/grids/cells/FlagColumn/FlagColumn';
import { useHeaderCell } from '../../../../.starryUI/grids/cells/HeaderCell/HeaderCell';
import { useLinkColumn } from '../../../../.starryUI/grids/cells/LinkColumn/LinkColumn';
import { useOptionsColumn } from '../../../../.starryUI/grids/cells/OptionsColumn/OptionsColumn';
import { usePhotoColumn } from '../../../../.starryUI/grids/cells/photoColumn/PhotoColumn';
import { useSelectionColumn } from '../../../../.starryUI/grids/cells/SelectionColumn/SelectionColumn';
import { SituationResponseFlagColumn } from '../../../../.starryUI/grids/cells/SituationResponseFlagColumn/SituationResponseFlagColumn';
import { IFooterComponentProps } from '../../../../.starryUI/grids/ServerPagedGrid/Footer';
import { IServerPagedGridRefs, ServerPagedGrid } from '../../../../.starryUI/grids/ServerPagedGrid/ServerPagedGrid';
import { ServerGridContextProvider } from '../../../../.starryUI/grids/ServerPagedGrid/ServerPageGridContext';
import { GetColumnStateFromGridFormat } from '../../../../.starryUI/grids/ServerPagedGrid/utils';
import { useSRWGlobalEvent } from '../../../../.starryUI/hooks/useSRWGlobalEvent';
import { ModuleLayout } from '../../../../.starryUI/layouts/moduleLayout';
import { avatarImagePopup, entryDirectoryCurrentPage, entryDirectoryPageAmount, entryDirectorySearchFor, entryDirectorySorting } from '../cache';
import { DirectoryFooter } from '../components/DirectoryFooter';
import { EntryOptions } from '../components/EntryOptions/EntryOptions';
import { EntrySummaryView, IEntrySummaryViewProps } from '../components/EntrySummaryView';
import { ExportDirectory } from '../components/ExportDirectory';
import { FixedFilters } from '../components/FixedFilters';
import { PhotoPopup } from '../components/PhotoPopup';
import { SaveView } from '../components/SaveView';
import { SaveViewProvider } from '../components/SaveView/context/SaveViewContext';
import { SituationResponseLabel } from '../components/SituationResponseLabel';
import { TooComplexBar } from '../components/TooComplexBar';
import { ViewPicker } from '../components/ViewPicker';
import { ViewPickerProvider } from '../components/ViewPicker/context/ViewPickerContext';
import { useAppContext } from '../context/AppContext';
import { useEntryDirectoryEvents } from '../context/EntryDirectoryContext';
import { IMainDirectoryListItem } from '../graphql/useAllMainDirectoryListQuery';
import { useLazyAllApplicationStatuses } from '../graphql/useLazyAllApplicationStatusQuery';
import { useLazyAllCategories } from '../graphql/useLazyAllCategoriesQuery';
import { useLazyAllCommunity } from '../graphql/useLazyAllCommunityQuery';
import { useLazyAllEntryStatus } from '../graphql/useLazyAllEntryStatusQuery';
import { useLazyAllGenders } from '../graphql/useLazyAllGendersQuery';
import { useLazyAllRoomTypes } from '../graphql/useLazyAllRoomTypesQuery';
import { useLazyAllTerm } from '../graphql/useLazyAllTermQuery';
import { useLazyAllTermSession } from '../graphql/useLazyAllTermSessionQuery';
import { useLazyAllUpcomingEvents } from '../graphql/useLazyAllUpcomingEventsQuery';
import { useLazyAllUpcomingGroups } from '../graphql/useLazyAllUpcomingGroupsQuery';
import {
    DirectoryFieldsEnum,
    IDirectoryFilter,
    IDirectoryNodes,
    IDirectoryVariablesSorting,
    useMainDirectoryItem,
    useMainDirectoryQuery,
    writeMainDirectoryCache,
} from '../graphql/useMainDirectoryQuery';
import { useMainDirectoryCache } from '../graphql/useMainDirectorySelectedQuery';
import { useUpdateMainDirectoryClearSelectedItems } from '../graphql/useUpdateMainDirectoryClearSelectedItems';
import { useUpdateMainDirectorySelectedItem } from '../graphql/useUpdateMainDirectorySelectedItem';
import { strings } from '../localization';
import { GetFilterName } from '../utils/stringUtil';

export const initialSorting: IDirectoryVariablesSorting = { field: 'NAME_LAST', order: 'ASC' };
export const filterStorageName = 'entry-directory-filters';
export const gridStorageName = 'entry-directory-grid-state';

export const EntryDirectory = () => {
    const { global, security } = useAppContext();
    const { newEntry, openEntry } = useEntryDirectoryEvents();
    const pageAmount = entryDirectoryPageAmount();
    const linkColumn = useLinkColumn<IEntrySummaryViewProps>(
        security.entry.canView ? (data) => openEntry(Number(data.id)) : undefined,
        security.entry.canView ? EntrySummaryView : undefined
    );
    const optionColumn = useOptionsColumn({
        ariaLabel: strings.MainDirectory_Label_FurtherOptions,
        title: strings.MainDirectory_Label_FurtherOptions,
        Component: EntryOptions,
    });
    const photoColumn = usePhotoColumn<IDirectoryNodes>((data) => ({
        src: data?.smallImage?.url,
        initials: data?.nameInitials,
        altText: data?.nameLast,
        onClick: () =>
            avatarImagePopup({
                visible: true,
                data,
            }),
    }));

    const { data, refetch, error } = useMainDirectoryQuery({
        skip: 0,
        limit: pageAmount,
        sort: [initialSorting],
        loadWorkSpace: false,
    });
    const getDirectoryItem = useMainDirectoryItem();
    const getApplicationStatuses = useLazyAllApplicationStatuses();
    const getTerms = useLazyAllTerm();
    const getCategories = useLazyAllCategories();
    const getCommunities = useLazyAllCommunity();
    const getEntryStatus = useLazyAllEntryStatus();
    const getGenders = useLazyAllGenders();
    const getRoomTypes = useLazyAllRoomTypes();
    const getTermSessions = useLazyAllTermSession();
    const getUpcomingEvents = useLazyAllUpcomingEvents();
    const getUpcomingGroups = useLazyAllUpcomingGroups();
    const gridRef = createRef<IServerPagedGridRefs>();
    const filterBarRef = createRef<IFilterBarRef>();
    const currentFilters = useRef<IDirectoryFilter[] | null>([]);
    const [mutateSelectedItem] = useUpdateMainDirectorySelectedItem();
    const [mutateClearSelection] = useUpdateMainDirectoryClearSelectedItems();
    const { cache } = useApolloClient();
    const { clearSelectedFromCache, updateSelectedInCache, getSelectedAmountFromCache, updateSelectedAmountInCache } = useMainDirectoryCache(cache);
    const headerCell = useHeaderCell<DirectoryFieldsEnum>(entryDirectorySorting, (key, order) => {
        gridRef.current?.gridApi()?.showLoadingOverlay();
        refetch({ sort: [{ field: key, order }] }).then(() => {
            gridRef.current?.gridApi()?.hideOverlay();
        });
    });

    useEffect(() => {
        entryDirectorySorting(initialSorting);
    }, []);

    useEffect(() => {
        if (gridRef.current) {
            // Store grid state for saving view
            storeColumnState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gridRef]);

    const selectionCell = useSelectionColumn((selected, data) => {
        let amount = getSelectedAmountFromCache();

        if (selected) {
            amount = amount + 1;
        } else {
            amount = amount - 1;
        }

        updateSelectedAmountInCache(amount);
        updateSelectedInCache(data.id, selected);
        mutateSelectedItem({ variables: { checked: selected, id: data.id } });
    }, alert);

    const handleClearSelection = () => {
        gridRef.current?.gridApi()?.forEachNode(({ data }) => {
            updateSelectedInCache(data.id, false);
        });

        clearSelectedFromCache();
        mutateClearSelection();
    };

    const handleNewEntry = () => {
        newEntry().then(() => {
            refetch();
        });
    };

    const handleShowSelectedChange = (value: boolean) => {
        if (!currentFilters.current) return;

        const showSelectedKey = 'selectedItemsOnly';
        const index = currentFilters.current.findIndex((f) => showSelectedKey in f);

        if (index > -1) {
            currentFilters.current[index][showSelectedKey]!.eq = value;
        } else {
            currentFilters.current = [...currentFilters.current, { [showSelectedKey]: { eq: value } }];
        }

        refetch({ filter: currentFilters.current });
    };

    useSRWGlobalEvent((_, event) => {
        if (event.Success && event.dbObjectName.startsWith('Entry')) {
            if (event.ID) {
                getDirectoryItem({ id: event.ID.toString() }).then(({ data }) => {
                    writeMainDirectoryCache(cache, data.mainDirectory);
                });
            } else if (event.Key.endsWith('UpdateSituationResponse')) {
                Promise.resolve(event).then(() => {
                    if (event.IDs) {
                        // after all events are fired
                        refetch();
                    }
                });
            }
        }
    }, 'EntryDirectory');

    const columnDefs: ColDef[] = [];

    columnDefs.push(
        {
            field: 'isSelected',
            ...selectionCell,
        },
        {
            headerName: '',
            // hide: true,
            field: 'image.url',
            width: 58,
            ...photoColumn,
        },
        {
            headerName: '',
            width: 30,
            ...optionColumn,
        },
        {
            headerName: '',
            width: 30,
            field: 'displayFlag',
            cellRendererFramework: FlagColumn,
        },
        {
            headerName: '',
            width: 30,
            sortable: false,
            field: 'situationResponseEnum',
            cellRendererFramework: SituationResponseFlagColumn,
        },
        {
            headerName: 'First Name',
            field: 'nameFirst',
            width: 150,
            ...linkColumn,
            ...headerCell('NAME_FIRST'),
        },
        {
            headerName: 'Last Name',
            field: 'nameLast',
            width: 150,
            ...linkColumn,
            ...headerCell('NAME_LAST'),
        },
        {
            headerName: 'Preferred Name',
            hide: true,
            width: 120,
            field: 'namePreferred',
            ...linkColumn,
            ...headerCell('NAME_PREFERRED'),
        },
        {
            headerName: 'Other Name',
            hide: true,
            width: 120,
            field: 'nameOther',
            ...linkColumn,
            ...headerCell('NAME_OTHER'),
        },
        {
            headerName: 'Email',
            width: 150,
            cellRendererFramework: EmailColumn,
            field: 'email',
            ...headerCell('EMAIL'),
        },
        {
            headerName: 'Room',
            width: 120,
            field: 'roomSpace_Description',
            ...headerCell('ROOM_SPACE_DESCRIPTION'),
        },
        {
            headerName: 'Extension',
            width: 100,
            field: 'extension_Description',
            hide: true,
            ...headerCell('EXTENSION_DESCRIPTION'),
        },
        {
            headerName: 'Virtual Extension',
            width: 100,
            hide: true,
            field: 'extension_Virtual',
            ...headerCell('EXTENSION_VIRTUAL'),
        },
        {
            headerName: 'Entry Status',
            width: 100,
            cellRendererFramework: EntryStatusColumn,
            field: 'entryStatus.value',
            ...headerCell('ENTRY_STATUS_DESCRIPTION'),
        },
        {
            headerName: 'Location',
            width: 100,
            field: 'roomLocation_Description',
            ...headerCell('ROOM_LOCATION_DESCRIPTION'),
        },
        {
            headerName: 'Room Type',
            field: 'roomType_Description',
            width: 100,
            hide: true,
            ...headerCell('ROOM_TYPE_DESCRIPTION'),
        },
        {
            headerName: 'Entry ID',
            field: 'entryID',
            width: 100,
            hide: true,
            ...linkColumn,
            ...headerCell('EVENT_ID'),
        }
    );

    security?.entry?.field?.iD1?.canView &&
        columnDefs.push({
            headerName: global?.setting?.iD1Label,
            width: 60,
            field: 'iD1',
            ...linkColumn,
            ...headerCell('I_D_1'),
        });

    security?.entry?.field?.iD2?.canView &&
        columnDefs.push({
            headerName: global?.setting?.iD2Label,
            field: 'iD2',
            width: 60,
            hide: true,
            ...linkColumn,
            ...headerCell('I_D_2'),
        });

    security?.entry?.field?.iD3?.canView &&
        columnDefs.push({
            headerName: global?.setting?.iD3Label,
            field: 'iD3',
            width: 60,
            hide: true,
            ...linkColumn,
            ...headerCell('I_D_3'),
        });

    security?.entry?.field?.iD4?.canView &&
        columnDefs.push({
            headerName: global?.setting?.iD4Label,
            field: 'iD4',
            width: 60,
            hide: true,
            ...linkColumn,
            ...headerCell('I_D_4'),
        });

    security?.entry?.field?.iD5?.canView &&
        columnDefs.push({
            headerName: global?.setting?.iD5Label,
            field: 'iD5',
            width: 60,
            hide: true,
            ...linkColumn,
            ...headerCell('I_D_5'),
        });

    columnDefs.push(
        {
            headerName: 'Group',
            field: 'group_Description',
            width: 60,
            ...headerCell('GROUP_DESCRIPTION'),
        },
        {
            headerName: 'Event',
            field: 'event_Description',
            width: 60,
            hide: true,
            ...headerCell('EVENT_DESCRIPTION'),
        },
        {
            headerName: 'Application Status',
            field: 'applicationStatus_Description',
            width: 60,
            hide: true,
            ...headerCell('APPLICATION_STATUS_DESCRIPTION'),
        },
        {
            headerName: 'Category Level 1',
            field: 'level1',
            ...headerCell('LEVEL_1'),
        },
        {
            headerName: 'Category Level 2',
            field: 'level2',
            hide: true,
            ...headerCell('LEVEL_2'),
        },
        {
            headerName: 'Category Level 3',
            field: 'level3',
            hide: true,
            ...headerCell('LEVEL_3'),
        },
        {
            headerName: 'Category Level 4',
            field: 'level4',
            hide: true,
            ...headerCell('LEVEL_4'),
        },
        {
            headerName: 'Category',
            field: 'category_Description',
            hide: true,
            ...headerCell('CATEGORY_DESCRIPTION'),
        },
        {
            headerName: 'Situation Response',
            field: 'situationResponse_Description',
            hide: true,
            ...headerCell('SITUATION_RESPONSE_DESCRIPTION'),
        },
        {
            headerName: 'Gender',
            field: 'genderEnum_Description',
            hide: true,
            ...headerCell('GROUP_DESCRIPTION'),
        },
        {
            headerName: 'Active Term Booking',
            field: 'termSession_Description',
            hide: true,
        },
        {
            headerName: 'Classification',
            field: 'classification_Description',
            hide: true,
            ...headerCell('CATEGORY_DESCRIPTION'),
        }
    );

    const handlePaginationClick = (skip: number, limit: number) => {
        localStorage.setItem('entryDirectoryPageAmount', limit.toString());
        return refetch({ skip, limit });
    };

    const handleFilterChange = (filters: ICurrentFilter[]) => {
        const currentFilter: IDirectoryFilter[] = [];

        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const filterValue = (item: any, useCaption?: boolean) =>
            filters
                .filter(item)
                .flatMap((e) => e.value)
                .map((e) => (useCaption ? e!.caption : e!.value));

        if (filters?.length) {
            const applicationStatusIDs = filterValue((e: ICurrentFilter) => e.name === 'Application Status');
            const appliedForTermIDs = filterValue((e: ICurrentFilter) => e.name === 'Applied For Term');
            const bookedForTermIDs = filterValue((e: ICurrentFilter) => e.name === 'Booking For Term');
            const termSessionIDs = filterValue((e: ICurrentFilter) => e.name === 'Active Term Booking');
            const entryStatus = filterValue((e: ICurrentFilter) => e.name === 'Entry Status');
            const genders = filterValue((e: ICurrentFilter) => e.name === 'Gender', true);
            const categoryIDs = filterValue((e: ICurrentFilter) => e.name === 'Category');
            const communityFilters = filterValue((e: ICurrentFilter) => e.name === 'Community');
            const roomTypeIDs = filterValue((e: ICurrentFilter) => e.name === 'Room Types');
            const eventIDs = filterValue((e: ICurrentFilter) => e.name === 'Upcoming Events');
            const groupIDs = filterValue((e: ICurrentFilter) => e.name === 'Upcoming Groups');

            if (applicationStatusIDs?.length) {
                currentFilter.push({ applicationStatusID: { in: applicationStatusIDs } });
            }
            if (appliedForTermIDs?.length) {
                currentFilter.push({ appliedForTermID: { in: appliedForTermIDs } });
            }
            if (bookedForTermIDs?.length) {
                currentFilter.push({ bookedForTermID: { in: bookedForTermIDs } });
            }
            if (termSessionIDs?.length) {
                currentFilter.push({ termSessionID: { in: termSessionIDs } });
            }
            if (entryStatus?.length) {
                currentFilter.push({ entryStatus: { in: entryStatus } });
            }
            if (genders?.length) {
                currentFilter.push({ gender: { in: genders } });
            }
            if (categoryIDs?.length) {
                currentFilter.push({ categoryID: { in: categoryIDs } });
            }
            if (communityFilters?.length) {
                currentFilter.push({ communityFilter: { in: communityFilters } });
            }
            if (roomTypeIDs?.length) {
                currentFilter.push({ roomTypeID: { in: roomTypeIDs } });
            }
            if (eventIDs?.length) {
                currentFilter.push({ eventID: { in: eventIDs } });
            }
            if (groupIDs?.length) {
                currentFilter.push({ groupID: { in: groupIDs } });
            }
        }
        gridRef.current?.gridApi()?.showLoadingOverlay();
        const otherFilters = currentFilters.current?.filter((f) => f.search || f.selectedItemsOnly);
        currentFilters.current = otherFilters ? [...currentFilter, ...otherFilters] : currentFilter;

        refetch({ filter: currentFilters.current, skip: 0 }).then(() => {
            gridRef.current?.gridApi()?.hideOverlay();
            sessionStorage.setItem(filterStorageName, JSON.stringify({ filters: currentFilter }));
        });
        entryDirectoryCurrentPage(1);
    };

    const handleFilterTypeAdd = (filter: ICurrentFilter): Promise<IData[]> => {
        return new Promise((resolve) => {
            switch (filter.name) {
                case 'Active Term Booking':
                    getTermSessions({}).then(({ data: filter }) => {
                        resolve(filter?.allTermSession.nodes.map((e) => ({ caption: e.caption, value: e.id })));
                    });
                    break;
                case 'Application Status':
                    getApplicationStatuses({}).then(({ data: filter }) => {
                        resolve(filter?.allApplicationStatuses.nodes.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
                case 'Applied For Term':
                    getTerms({}).then(({ data: filter }) => {
                        resolve(filter?.allTerm.nodes.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
                case 'Booking For Term':
                    getTerms({}).then(({ data: filter }) => {
                        resolve(filter?.allTerm.nodes.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
                case 'Category':
                    getCategories({}).then(({ data: filter }) => {
                        resolve(filter?.allCategories.nodes.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
                case 'Community':
                    getCommunities({}).then(({ data: filter }) => {
                        resolve(filter?.allCommunityFilterOptions.map((e) => ({ caption: e.description, value: e.enumValue })));
                    });
                    break;
                case 'Entry Status':
                    getEntryStatus({}).then(({ data: filter }) => {
                        resolve(filter?.allEntryStatus.map((e) => ({ caption: e.description, value: e.enumValue })));
                    });
                    break;
                case 'Gender':
                    getGenders({}).then(({ data: filter }) => {
                        resolve(filter?.allGenders.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
                case 'Room Types':
                    getRoomTypes({}).then(({ data: filter }) => {
                        resolve(filter?.allRoomTypes.nodes.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
                case 'Upcoming Events':
                    getUpcomingEvents({}).then(({ data: filter }) => {
                        resolve(filter?.allUpcomingEvents.nodes.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
                case 'Upcoming Groups':
                    getUpcomingGroups({}).then(({ data: filter }) => {
                        resolve(filter?.allUpcomingGroups.nodes.map((e) => ({ caption: e.description, value: e.id })));
                    });
                    break;
            }
        });
    };

    const handleOnRefresh = () => {
        const ref = gridRef.current?.gridApi();

        ref?.showLoadingOverlay();
        refetch({ skip: 0 }).then(() => {
            ref?.refreshCells({ force: true });
            ref?.hideOverlay();
        });
        entryDirectoryCurrentPage(1);
    };

    const handleOnClear = () => {
        // Clear filters only, and retain the search && showSelectedOnly setting
        filterBarRef.current?.clearFilters();
        const remainingFilters = currentFilters.current?.filter((f) => f.search || f.selectedItemsOnly);
        currentFilters.current = remainingFilters ? [...remainingFilters] : [];
        refetch({ filter: currentFilters.current, skip: 0 });
    };

    const handleOnSearch = (searchString: string) => {
        const otherFilters = currentFilters.current?.filter((f) => !f.search);
        const searchFilter = { search: { like: searchString } };
        currentFilters.current = otherFilters ? [...otherFilters, searchFilter] : [searchFilter];
        entryDirectorySearchFor(searchString);
        const ref = gridRef.current?.gridApi();
        ref?.showLoadingOverlay();
        refetch({ filter: currentFilters.current, skip: 0 }).then(() => {
            ref?.refreshCells({ force: true });
            ref?.hideOverlay();
        });
    };

    const handleFilterSearchChange = (filter: FilterChange) => {
        switch (filter.type) {
            case 'Clear':
                handleOnClear();
                break;

            case 'Search':
                handleOnSearch(filter.searchString);
                break;
        }

        entryDirectoryCurrentPage(1);
    };

    const handleResetColumns = () => {
        gridRef.current?.gridApi()?.setColumnDefs([]);
        gridRef.current?.gridApi()?.setColumnDefs(columnDefs);
    };

    const buildColumns = (): IManagedColumn[] => {
        const cols: IManagedColumn[] = [];

        columnDefs
            .filter((e) => e.headerName)
            .forEach((e) => {
                switch (e.field) {
                    case 'image.url':
                        break;
                    case 'displayFlag':
                        break;

                    default:
                        cols.push({
                            caption: e.headerName!,
                            visible: !e.hide,
                            field: e.field!,
                        });
                }
            });

        cols.push({
            caption: strings.MainDirectory_Label_Flag,
            visible: true,
            field: 'displayFlag',
        });

        cols.push({
            caption: strings.MainDirectory_Label_EntryPhoto,
            visible: true,
            field: 'image.url',
        });

        return cols;
    };

    const storeColumnState = () => {
        const columnState = gridRef.current?.columnApi()?.getColumnState();

        if (columnState?.length) {
            sessionStorage.setItem(
                gridStorageName,
                JSON.stringify(
                    columnState.map((col: ColumnState, i: number) => ({
                        id: col.colId,
                        columnVisibility: !col.hide,
                        columnWidth: col.width,
                        columnOrder: i,
                    }))
                )
            );
        }
    };

    const handleColumnToggle = (column: IManagedColumn) => {
        const col = gridRef.current
            ?.columnApi()
            ?.getAllColumns()
            ?.find((e) => e.getColDef().field === column.field);
        gridRef?.current?.columnApi()?.setColumnVisible(col?.getColId() as string, column.visible);
    };

    // TODO move this interface
    const handleViewPickerOnChange = (viewItem: IMainDirectoryListItem) => {
        const filterBarref = filterBarRef.current;
        const columnApi = gridRef?.current?.columnApi();
        const gridApi = gridRef?.current?.gridApi();

        gridApi?.showLoadingOverlay();

        refetch({ viewId: Number(viewItem.id), viewType: viewItem.viewType, loadWorkSpace: viewItem.viewType === 'DYNAMIC_LIST' }).then(
            ({ data }) => {
                gridApi?.hideOverlay();

                // load filters
                if (data?.mainDirectoryWorkspace?.isTooComplex || !data?.mainDirectoryWorkspace?.filters) {
                    filterBarref?.clearFilters();
                } else {
                    const filters: IAvailableFilter[] = data?.mainDirectoryWorkspace?.filters.map((f) => ({
                        multiple: f.values.length > 1,
                        name: GetFilterName(f.key),
                        value: f.values.map((v) => ({
                            caption: v.description,
                            value: v.itemId,
                        })),
                    }));

                    if (filters && filters.length > 0) {
                        filterBarref?.setFilters(filters);
                    }
                }

                // load grid format
                if (data?.mainDirectoryWorkspace?.gridFormat) {
                    const gridFormat = data?.mainDirectoryWorkspace?.gridFormat;
                    const currentColState = columnApi?.getColumnState();
                    if (currentColState) {
                        columnApi?.applyColumnState({ state: GetColumnStateFromGridFormat(currentColState, gridFormat), applyOrder: true });
                    }
                }
            }
        );
    };

    const handleSaveView = (id: string) => {
        gridRef.current?.gridApi()?.showLoadingOverlay();
        refetch({ viewId: Number(id), viewType: 'DYNAMIC_LIST' }).then(() => {
            gridRef.current?.gridApi()?.hideOverlay();
        });
    };

    const handleColumnInteraction = () => {
        storeColumnState();
    };

    if (error) return <ErrorDisplay error={error} />;

    return (
        <ServerGridContextProvider
            value={{
                currentPageCache: entryDirectoryCurrentPage,
                pageAmount: entryDirectoryPageAmount,
                getCurrentHighlight: entryDirectorySearchFor,
                totalRecords: data?.allMainDirectory?.totalCount,
            }}
        >
            <ModuleLayout
                caption={
                    <HStack gap="small" justifyContent="flex-start">
                        <ViewPickerProvider onChange={handleViewPickerOnChange} currentView={data?.allMainDirectory?.currentView}>
                            <ViewPicker />
                        </ViewPickerProvider>
                        <SituationResponseLabel count={data?.allMainDirectory?.totalNotOkayCount} />
                    </HStack>
                }
                subCaption={
                    <Text color={color.Grey.G60} variant="normal.regular">
                        {data?.allMainDirectory.totalCount} items
                    </Text>
                }
                buttonBar={
                    <ButtonBar OnFilterSearchChange={handleFilterSearchChange}>
                        <RefreshButton disabled={false} onClick={() => handleOnRefresh()} />
                        <ExportDirectory />
                        <ManageColumnButton columns={buildColumns()} onClick={handleColumnToggle} onResetColumns={handleResetColumns} />
                    </ButtonBar>
                }
                newButtonCaption={strings.MainDirectory_Label_NewEntry}
                onNewButtonClick={handleNewEntry}
                filterControls={
                    <HStack inline gap="xxSmall">
                        {data?.mainDirectoryWorkspace?.isTooComplex ? (
                            <TooComplexBar />
                        ) : (
                            security.entry.filter.canUpdateFilters && (
                                <>
                                    {data?.allMainDirectory?.fixedFilters && <FixedFilters filters={data?.allMainDirectory?.fixedFilters} />}
                                    <FilterBar ref={filterBarRef} showSearch onChange={handleFilterChange} onFilterTypeAdd={handleFilterTypeAdd}>
                                        <BarFilter name={strings.MainDirectory_Label_ActiveTermBooking} type="MultipleSelect" />
                                        <BarFilter name={strings.MainDirectory_Label_ApplicationStatus} type="MultipleSelect" />
                                        <BarFilter name={strings.MainDirectory_Label_AppliedForTerm} type="MultipleSelect" />
                                        <BarFilter name={strings.MainDirectory_Label_BookingForTerm} type="MultipleSelect" />
                                        <BarFilter name={strings.MainDirectory_Label_Category} type="MultipleSelect" />
                                        <BarFilter name={strings.MainDirectory_Label_Community} type="MultipleSelect" />
                                        <BarFilter name={strings.MainDirectory_Label_EntryStatus} type="MultipleSelect" />
                                        <BarFilter name={strings.MainDirectory_Label_Gender} type="MultipleSelect" />
                                        <BarFilter name={strings.MainDirectory_Label_RoomTypes} type="MultipleSelect" />
                                        <BarFilter name={strings.MainDirectory_Label_UpcomingEvents} type="MultipleSelect" />
                                        <BarFilter name={strings.MainDirectory_Label_UpcomingGroups} type="MultipleSelect" />
                                    </FilterBar>
                                    <SaveViewProvider onSave={handleSaveView} currentView={data?.allMainDirectory?.currentView}>
                                        <SaveView />
                                    </SaveViewProvider>
                                </>
                            )
                        )}
                    </HStack>
                }
            >
                {({ height }) => (
                    <div style={{ height: '100%' }}>
                        <ServerPagedGrid
                            ref={gridRef}
                            stickyTop={`${height}px`}
                            initialData={data?.allMainDirectory?.nodes}
                            immutableData
                            onMoreDataRequest={handlePaginationClick}
                            columnDefs={columnDefs}
                            rowData={data?.allMainDirectory.nodes}
                            rowSelection="multiple"
                            suppressRowClickSelection={true}
                            onCellClicked={() => {
                                /* empty */
                            }}
                            footerComponent={DirectoryFooter as React.FC<IFooterComponentProps>}
                            footerComponentProps={{
                                onClearSelection: handleClearSelection,
                                onShowSelected: handleShowSelectedChange,
                            }}
                            handleColumnInteraction={handleColumnInteraction}
                        />
                    </div>
                )}
            </ModuleLayout>

            <PhotoPopup />
        </ServerGridContextProvider>
    );
};
